import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import Links from "../../Data/Links";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import "./ConsolidatedMtdReport.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const COLUMNS = [
  { key: "name", label: "Dealership" },
  { key: "gadsBudget", label: "Budget" },
  { key: "gadsSpent", label: "Spent" },
  { key: "gadsLeadGoal", label: "Lead Goal" },
  { key: "gadsLeads", label: "Leads" },
  { key: "gadsCpl", label: "CPL" },
  { key: "metaBudget", label: "Budget" },
  { key: "metaSpent", label: "Spent" },
  { key: "metaLeadGoal", label: "Lead Goal" },
  { key: "metaLeads", label: "Leads" },
  { key: "metaCpl", label: "CPL" }
];

const GADS_COLUMNS = ["gadsBudget", "gadsSpent", "gadsLeadGoal", "gadsLeads", "gadsCpl"];
const META_COLUMNS = ["metaBudget", "metaSpent", "metaLeadGoal", "metaLeads", "metaCpl"];

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const toTargetNumber = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatNumberOne = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const normalizeStoreKey = (value) => String(value || "").trim().toLowerCase();

const getStoreName = (row) => {
  return (
    row?.store_name ||
    row?.STORE_NAME ||
    row?.store ||
    row?.dealership ||
    row?.name ||
    row?.storeName ||
    "-"
  );
};

const getDivisionLabel = (row) => {
  return (
    row?.division ||
    row?.store_division ||
    row?.storeDivision ||
    row?.Division ||
    ""
  ).trim();
};

const usedDivisions = ["DriveNation", "FFUN Cars", "Used", "Carooga", "FFUN Group", "Group"];
const usedStoreNames = ["Carooga", "FFUN Group"];

const normalizeDivision = (divisionValue) => {
  const division = (divisionValue || "").trim();
  if (!division) return "";
  if (usedDivisions.includes(division)) return "Used";
  return "OEM";
};

const getDivisionGroup = (row) => {
  const label = getDivisionLabel(row);
  if (label.toLowerCase() === "group") return "Group";
  const normalized = normalizeDivision(label);
  if (normalized) return normalized;

  const storeName = getStoreName(row);
  if (storeName) {
    const normalizedStore = storeName.trim().toLowerCase();
    const isUsedStore = usedStoreNames.some(
      (name) => name.toLowerCase() === normalizedStore || normalizedStore.includes(name.toLowerCase())
    );
    if (isUsedStore) return "Used";
  }

  return "OEM";
};

const getTargetsDivision = (row) => {
  const label = getDivisionLabel(row);
  if (label.toLowerCase() === "group") return "Used";
  const normalized = normalizeDivision(label);
  return normalized || "OEM";
};

export default function ConsolidatedMtdReport() {
  const [googleRows, setGoogleRows] = useState([]);
  const [metaRows, setMetaRows] = useState([]);
  const [targetsByStore, setTargetsByStore] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedStores, setSelectedStores] = useState(new Set());
  const [visibleColumns, setVisibleColumns] = useState(new Set(COLUMNS.map((col) => col.key)));
  const [storeMenuOpen, setStoreMenuOpen] = useState(false);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const storeMenuRef = useRef(null);
  const columnMenuRef = useRef(null);
  const hasInitializedStores = useRef(false);

  const startDate = useMemo(() => moment().startOf("month"), []);
  const endDate = useMemo(() => {
    const yesterday = moment().subtract(1, "day");
    return yesterday.isBefore(startDate) ? startDate.clone() : yesterday;
  }, [startDate]);

  const totalDays = endDate.daysInMonth();
  const elapsedDays = endDate.date();
  const paceFactor = totalDays ? elapsedDays / totalDays : 0;

  const dateLabel = `${startDate.format("MMM D")} - ${endDate.format("D, YYYY")}`;
  const targetMonth = startDate.format("YYYY-MM");

  const fetchRows = () => {
    setLoading(true);
    setError("");

    const payload = {
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    };

    Promise.allSettled([
      axios.post(Links.googleAdsDivisionsDaily, payload),
      axios.post(Links.metaAdsDivisionsDaily, payload)
    ])
      .then(([gadsResult, metaResult]) => {
        const gadsList = gadsResult.status === "fulfilled" ? gadsResult.value?.data?.data || gadsResult.value?.data || [] : [];
        const metaList = metaResult.status === "fulfilled" ? metaResult.value?.data?.data || metaResult.value?.data || [] : [];
        setGoogleRows(Array.isArray(gadsList) ? gadsList : []);
        setMetaRows(Array.isArray(metaList) ? metaList : []);
        if (gadsResult.status === "rejected" && metaResult.status === "rejected") {
          setError("Failed to load consolidated MTD data.");
        }
      })
      .catch((err) => {
        console.log("Consolidated MTD fetch error:", err);
        setGoogleRows([]);
        setMetaRows([]);
        setError("Failed to load consolidated MTD data.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    const combined = [...googleRows, ...metaRows];
    if (combined.length === 0) {
      setTargetsByStore(new Map());
      return;
    }

    const storeDivisionPairs = new Map();
    combined.forEach((row) => {
      const storeName = getStoreName(row);
      if (!storeName || storeName === "-") return;
      const storeKey = normalizeStoreKey(storeName);
      if (!storeDivisionPairs.has(storeKey)) {
        storeDivisionPairs.set(storeKey, {
          name: storeName,
          division: getTargetsDivision(row)
        });
      }
    });

    const entries = Array.from(storeDivisionPairs.entries());
    if (entries.length === 0) {
      setTargetsByStore(new Map());
      return;
    }

    Promise.allSettled(
      entries.map(([, meta]) =>
        axios.post(Links.marketingTargets, {
          division: meta.division,
          dealer: meta.name,
          month: targetMonth
        })
      )
    )
      .then((results) => {
        const next = new Map();
        results.forEach((result, index) => {
          if (result.status !== "fulfilled") return;
          const storeKey = entries[index][0];
          const payload = result.value?.data?.data ?? result.value?.data ?? {};
          const targetRow = Array.isArray(payload) ? payload[0] : payload;
          if (!targetRow) return;

          const gadsBudget = toTargetNumber(
            targetRow?.google_ads_budget || targetRow?.budget || targetRow?.gads_budget
          );
          const gadsLeadGoal = toTargetNumber(
            targetRow?.google_ads_leads_goal || targetRow?.leads_goal || targetRow?.gads_leads_goal
          );
          const metaBudget = toTargetNumber(
            targetRow?.meta_ads_budget || targetRow?.fb_ads_budget || targetRow?.meta_budget
          );
          const metaLeadGoal = toTargetNumber(
            targetRow?.meta_ads_leads_goal || targetRow?.fb_ads_leads_goal || targetRow?.meta_leads_goal
          );

          if (
            gadsBudget !== undefined ||
            gadsLeadGoal !== undefined ||
            metaBudget !== undefined ||
            metaLeadGoal !== undefined
          ) {
            next.set(storeKey, { gadsBudget, gadsLeadGoal, metaBudget, metaLeadGoal });
          }
        });
        setTargetsByStore(next);
      })
      .catch((err) => {
        console.log("Consolidated targets error:", err);
        setTargetsByStore(new Map());
      });
  }, [googleRows, metaRows, targetMonth]);

  const aggregatedRows = useMemo(() => {
    const map = new Map();

    const ensureEntry = (store, row) => {
      if (!map.has(store)) {
        map.set(store, {
          store,
          divisionGroup: getDivisionGroup(row),
          gadsBudget: 0,
          gadsSpent: 0,
          gadsLeadGoal: 0,
          gadsLeads: 0,
          metaBudget: 0,
          metaSpent: 0,
          metaLeadGoal: 0,
          metaLeads: 0
        });
      }
      return map.get(store);
    };

    googleRows.forEach((row) => {
      const store = getStoreName(row);
      if (!store || store === "-") return;
      const entry = ensureEntry(store, row);
      entry.gadsSpent += toNumber(row?.spend ?? row?.cost ?? row?.amount_spent);
      entry.gadsLeads += toNumber(row?.leads ?? row?.LEADS);
    });

    metaRows.forEach((row) => {
      const store = getStoreName(row);
      if (!store || store === "-") return;
      const entry = ensureEntry(store, row);
      entry.metaSpent += toNumber(row?.spend ?? row?.cost ?? row?.amount_spent);
      entry.metaLeads += toNumber(row?.leads ?? row?.LEADS);
    });

    const withTargets = Array.from(map.values()).map((entry) => {
      const targets = targetsByStore.get(normalizeStoreKey(entry.store));
      if (targets) {
        if (targets.gadsBudget !== undefined) entry.gadsBudget = targets.gadsBudget;
        if (targets.gadsLeadGoal !== undefined) entry.gadsLeadGoal = targets.gadsLeadGoal;
        if (targets.metaBudget !== undefined) entry.metaBudget = targets.metaBudget;
        if (targets.metaLeadGoal !== undefined) entry.metaLeadGoal = targets.metaLeadGoal;
      }
      return entry;
    });

    return withTargets.map((entry) => {
      const gadsCpl = entry.gadsLeads ? entry.gadsSpent / entry.gadsLeads : 0;
      const metaCpl = entry.metaLeads ? entry.metaSpent / entry.metaLeads : 0;
      return { ...entry, gadsCpl, metaCpl };
    });
  }, [googleRows, metaRows, targetsByStore]);

  useEffect(() => {
    if (hasInitializedStores.current || aggregatedRows.length === 0) return;
    setSelectedStores(new Set(aggregatedRows.map((row) => row.store)));
    hasInitializedStores.current = true;
  }, [aggregatedRows]);

  const filteredRows = useMemo(() => {
    if (selectedStores.size === 0) return [];
    return aggregatedRows.filter((row) => selectedStores.has(row.store));
  }, [aggregatedRows, selectedStores]);

  const calculateTotals = (items) => {
    const totals = items.reduce(
      (acc, row) => {
        acc.gadsBudget += row.gadsBudget;
        acc.gadsSpent += row.gadsSpent;
        acc.gadsLeadGoal += row.gadsLeadGoal;
        acc.gadsLeads += row.gadsLeads;
        acc.metaBudget += row.metaBudget;
        acc.metaSpent += row.metaSpent;
        acc.metaLeadGoal += row.metaLeadGoal;
        acc.metaLeads += row.metaLeads;
        return acc;
      },
      {
        gadsBudget: 0,
        gadsSpent: 0,
        gadsLeadGoal: 0,
        gadsLeads: 0,
        metaBudget: 0,
        metaSpent: 0,
        metaLeadGoal: 0,
        metaLeads: 0
      }
    );

    const gadsCpl = totals.gadsLeads ? totals.gadsSpent / totals.gadsLeads : 0;
    const metaCpl = totals.metaLeads ? totals.metaSpent / totals.metaLeads : 0;

    return { ...totals, gadsCpl, metaCpl };
  };

  const sections = useMemo(() => {
    const oemRows = filteredRows.filter((row) => row.divisionGroup === "OEM");
    const usedRows = filteredRows.filter((row) => row.divisionGroup === "Used");
    const groupRows = filteredRows.filter((row) => row.divisionGroup === "Group");
    return [
      { key: "OEM DIVISION", rows: oemRows },
      { key: "USED DIVISION", rows: usedRows },
      { key: "GROUP", rows: groupRows }
    ];
  }, [filteredRows]);

  useEffect(() => {
    if (!storeMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (storeMenuRef.current && !storeMenuRef.current.contains(event.target)) {
        setStoreMenuOpen(false);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, [storeMenuOpen]);

  useEffect(() => {
    if (!columnMenuOpen) return undefined;
    const handleOutside = (event) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setColumnMenuOpen(false);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, [columnMenuOpen]);

  const storeGroups = useMemo(() => {
    const groups = {
      "OEM Division": [],
      "Used Division": [],
      Group: []
    };
    aggregatedRows.forEach((row) => {
      const key = row.divisionGroup === "Used" ? "Used Division" : row.divisionGroup === "Group" ? "Group" : "OEM Division";
      groups[key].push(row.store);
    });
    return groups;
  }, [aggregatedRows]);

  const visibleColumnList = useMemo(
    () => COLUMNS.filter((col) => visibleColumns.has(col.key)),
    [visibleColumns]
  );

  const visibleGadsColumns = useMemo(
    () => GADS_COLUMNS.filter((key) => visibleColumns.has(key)),
    [visibleColumns]
  );

  const visibleMetaColumns = useMemo(
    () => META_COLUMNS.filter((key) => visibleColumns.has(key)),
    [visibleColumns]
  );

  const hasSubheader = useMemo(
    () => visibleColumnList.some((col) => col.key !== "name"),
    [visibleColumnList]
  );

  const storeLabel = useMemo(() => {
    if (selectedStores.size === 0) return "None selected";
    if (selectedStores.size === aggregatedRows.length && aggregatedRows.length > 0) return "All Dealerships";
    if (selectedStores.size <= 3) return Array.from(selectedStores).join(", ");
    return `${selectedStores.size} dealerships selected`;
  }, [selectedStores, aggregatedRows.length]);

  const columnLabel = useMemo(() => {
    const toggleableCount = COLUMNS.length - 1;
    const shownCount = visibleColumns.has("name") ? visibleColumns.size - 1 : visibleColumns.size;
    if (shownCount === toggleableCount) return "All Columns";
    if (shownCount <= 0) return "Name only";
    return `${shownCount} of ${toggleableCount} columns`;
  }, [visibleColumns]);

  const leadPctClass = (value) => {
    if (value === null || value === undefined) return "";
    if (value >= 80) return "clr-green";
    if (value >= 60) return "clr-yellow";
    return "clr-red";
  };

  const cplClass = (current, goal) => {
    if (!current || !goal) return "";
    const ratio = current / goal;
    if (ratio <= 1) return "clr-green";
    if (ratio <= 1.15) return "clr-yellow";
    return "clr-red";
  };

  const paceClass = (value) => {
    if (value === null || value === undefined) return "";
    const abs = Math.abs(value);
    if (abs <= 10) return "clr-green";
    if (abs <= 25) return "clr-yellow";
    return "clr-red";
  };

  const formatPace = (value) => {
    if (value === null || value === undefined) return "-";
    const label = value > 0 ? "Over" : "Under";
    const sign = value > 0 ? "+" : "";
    return `${label} ${sign}${value.toFixed(1)}%`;
  };

  const leadsChartData = useMemo(() => {
    const labels = filteredRows.map((row) => row.store);
    const combinedGoal = filteredRows.map((row) => (row.gadsLeadGoal + row.metaLeadGoal) || 0);
    const pacedGoals = combinedGoal.map((goal) => (goal ? Number((goal * paceFactor).toFixed(1)) : 0));
    const gadsLeads = filteredRows.map((row) => row.gadsLeads || 0);
    const metaLeads = filteredRows.map((row) => row.metaLeads || 0);

    return {
      labels,
      datasets: [
        {
          label: "Paced Goal",
          data: pacedGoals,
          backgroundColor: "rgba(65,64,66,0.18)",
          borderColor: "rgba(65,64,66,0.55)",
          borderWidth: 1,
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        },
        {
          label: "GAds Leads",
          data: gadsLeads,
          backgroundColor: "rgba(76,124,245,0.82)",
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        },
        {
          label: "Meta Leads",
          data: metaLeads,
          backgroundColor: "rgba(138,98,214,0.82)",
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75
        }
      ]
    };
  }, [filteredRows, paceFactor]);

  const leadsChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: { font: { family: "Rubik", size: 11 }, boxWidth: 14 }
        },
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.x.toFixed(1)} leads`
          }
        }
      },
      scales: {
        x: {
          grid: { color: "#f0f0f0" },
          ticks: { font: { family: "Rubik", size: 11 } },
          title: { display: true, text: "Leads", font: { family: "Rubik", size: 12 } }
        },
        y: {
          grid: { display: false },
          ticks: { font: { family: "Rubik", size: 11 } }
        }
      }
    }),
    []
  );

  const cplChartData = useMemo(() => {
    const labels = filteredRows.map((row) => row.store);
    const gadsCpl = filteredRows.map((row) => row.gadsCpl || 0);
    const metaCpl = filteredRows.map((row) => row.metaCpl || 0);

    return {
      labels,
      datasets: [
        {
          label: "GAds CPL",
          data: gadsCpl,
          backgroundColor: "rgba(76,124,245,0.82)",
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75,
          order: 2
        },
        {
          label: "Meta CPL",
          data: metaCpl,
          backgroundColor: "rgba(138,98,214,0.82)",
          borderRadius: 4,
          barPercentage: 0.75,
          categoryPercentage: 0.75,
          order: 1
        }
      ]
    };
  }, [filteredRows]);

  const cplChartOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
          labels: { font: { family: "Rubik", size: 11 }, boxWidth: 14 }
        },
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.x.toFixed(2)}`
          }
        }
      },
      scales: {
        x: {
          grid: { color: "#f0f0f0" },
          ticks: { callback: (value) => `$${value}`, font: { family: "Rubik", size: 11 } },
          title: { display: true, text: "Cost Per Lead ($)", font: { family: "Rubik", size: 12 } }
        },
        y: {
          grid: { display: false },
          ticks: { font: { family: "Rubik", size: 11 } }
        }
      }
    }),
    []
  );

  return (
    <div className="consolidated-dashboard">
      <header className="consolidated-header">
        <h1>
          <span>FFUN</span> Group | Consolidated MTD
        </h1>
        <div className="consolidated-header-meta">
          <div>{dateLabel}</div>
        </div>
      </header>

      <div className="consolidated-container">
        <div className="consolidated-controls">
          <label>Dealerships:</label>
          <div className="consolidated-dropdown" ref={storeMenuRef}>
            <button
              type="button"
              className="consolidated-dropdown-btn"
              onClick={() => setStoreMenuOpen((prev) => !prev)}
            >
              {storeLabel}
            </button>
            <div className={`consolidated-dropdown-menu ${storeMenuOpen ? "open" : ""}`}>
              <div className="consolidated-dropdown-actions">
                <button
                  type="button"
                  onClick={() => setSelectedStores(new Set(aggregatedRows.map((row) => row.store)))}
                >
                  Select All
                </button>
                <button type="button" onClick={() => setSelectedStores(new Set())}>
                  Clear
                </button>
              </div>
              <div className="consolidated-dropdown-divider" />
              <div className="consolidated-dropdown-section">OEM Division</div>
              {storeGroups["OEM Division"].map((name) => {
                const checked = selectedStores.has(name);
                return (
                  <label className="consolidated-dropdown-item" key={`oem-${name}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedStores);
                        if (checked) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        setSelectedStores(next);
                      }}
                    />
                    {name}
                  </label>
                );
              })}
              <div className="consolidated-dropdown-divider" />
              <div className="consolidated-dropdown-section">Used Division</div>
              {storeGroups["Used Division"].map((name) => {
                const checked = selectedStores.has(name);
                return (
                  <label className="consolidated-dropdown-item" key={`used-${name}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedStores);
                        if (checked) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        setSelectedStores(next);
                      }}
                    />
                    {name}
                  </label>
                );
              })}
              <div className="consolidated-dropdown-divider" />
              <div className="consolidated-dropdown-section">Group</div>
              {storeGroups["Group"].map((name) => {
                const checked = selectedStores.has(name);
                return (
                  <label className="consolidated-dropdown-item" key={`group-${name}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(selectedStores);
                        if (checked) {
                          next.delete(name);
                        } else {
                          next.add(name);
                        }
                        setSelectedStores(next);
                      }}
                    />
                    {name}
                  </label>
                );
              })}
            </div>
          </div>

          <label>Columns:</label>
          <div className="consolidated-dropdown" ref={columnMenuRef}>
            <button
              type="button"
              className="consolidated-dropdown-btn"
              onClick={() => setColumnMenuOpen((prev) => !prev)}
            >
              {columnLabel}
            </button>
            <div className={`consolidated-dropdown-menu ${columnMenuOpen ? "open" : ""}`}>
              <div className="consolidated-dropdown-actions">
                <button
                  type="button"
                  onClick={() => setVisibleColumns(new Set(COLUMNS.map((col) => col.key)))}
                >
                  Show All
                </button>
                <button
                  type="button"
                  onClick={() => setVisibleColumns(new Set(["name"]))}
                >
                  Hide All
                </button>
              </div>
              <div className="consolidated-dropdown-divider" />
              {COLUMNS.filter((col) => col.key !== "name").map((col) => {
                const checked = visibleColumns.has(col.key);
                return (
                  <label className="consolidated-dropdown-item" key={col.key}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {
                        const next = new Set(visibleColumns);
                        if (checked) {
                          next.delete(col.key);
                        } else {
                          next.add(col.key);
                        }
                        setVisibleColumns(next);
                      }}
                    />
                    {col.label}
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {error && <div className="consolidated-error">{error}</div>}
        {loading && <div className="consolidated-loading">Loading consolidated data...</div>}

        <div className="consolidated-table-wrap">
          <table>
            <thead>
              <tr className="consolidated-header-row">
                {visibleColumns.has("name") && (
                  <th rowSpan={hasSubheader ? 2 : 1}>Dealership</th>
                )}
                {visibleGadsColumns.length > 0 && (
                  <th colSpan={visibleGadsColumns.length} className="consolidated-group-title google">
                    Google Ads
                  </th>
                )}
                {visibleMetaColumns.length > 0 && (
                  <th colSpan={visibleMetaColumns.length} className="consolidated-group-title meta">
                    Meta Ads
                  </th>
                )}
              </tr>
              {hasSubheader && (
                <tr className="consolidated-subheader-row">
                  {visibleColumnList
                    .filter((col) => col.key !== "name")
                    .map((col) => {
                      const boundaryClass =
                        col.key === "gadsBudget"
                          ? "boundary-left"
                          : col.key === "metaBudget"
                          ? "boundary-meta"
                          : "";
                      return (
                        <th key={col.key} className={boundaryClass}>
                          {col.label}
                        </th>
                      );
                    })}
                </tr>
              )}
            </thead>
            <tbody>
              {filteredRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={visibleColumnList.length} className="consolidated-empty">
                    No consolidated data found.
                  </td>
                </tr>
              ) : (
                sections.map((section) => (
                  <React.Fragment key={section.key}>
                    {section.rows.length > 0 && (
                      <tr className="consolidated-division">
                        <td colSpan={visibleColumnList.length}>{section.key}</td>
                      </tr>
                    )}
                    {section.rows.map((row) => (
                      <tr key={row.store}>
                        {visibleColumnList.map((col) => {
                          let display = row[col.key];
                          if (col.key === "name") display = row.store;
                          if (col.key === "gadsBudget") display = formatCurrency(row.gadsBudget);
                          if (col.key === "gadsSpent") display = formatCurrency(row.gadsSpent);
                          if (col.key === "gadsLeadGoal") display = formatNumber(row.gadsLeadGoal);
                          if (col.key === "gadsLeads") display = formatNumberOne(row.gadsLeads);
                          if (col.key === "gadsCpl") display = formatCurrency(row.gadsCpl);
                          if (col.key === "metaBudget") display = formatCurrency(row.metaBudget);
                          if (col.key === "metaSpent") display = formatCurrency(row.metaSpent);
                          if (col.key === "metaLeadGoal") display = formatNumber(row.metaLeadGoal);
                          if (col.key === "metaLeads") display = formatNumberOne(row.metaLeads);
                          if (col.key === "metaCpl") display = formatCurrency(row.metaCpl);

                          let className = "";
                          if (col.key === "gadsCpl") className = cplClass(row.gadsCpl, row.gadsLeadGoal ? row.gadsBudget / row.gadsLeadGoal : 0);
                          if (col.key === "metaCpl") className = cplClass(row.metaCpl, row.metaLeadGoal ? row.metaBudget / row.metaLeadGoal : 0);

                          const boundaryClass =
                            col.key === "gadsBudget"
                              ? "boundary-left"
                              : col.key === "metaBudget"
                              ? "boundary-meta"
                              : "";

                          return (
                            <td key={col.key} className={`${className} ${boundaryClass}`.trim()}>
                              {display}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {section.rows.length > 0 && (() => {
                      // Compute totals once per section to avoid repeating reductions per cell.
                      const totals = calculateTotals(section.rows);

                      return (
                        <tr className="consolidated-total">
                          {visibleColumnList.map((col) => {
                            let display = totals[col.key];
                            if (col.key === "name") display = `${section.key.split(" ")[0]} Total`;
                            if (col.key === "gadsBudget") display = formatCurrency(totals.gadsBudget);
                            if (col.key === "gadsSpent") display = formatCurrency(totals.gadsSpent);
                            if (col.key === "gadsLeadGoal") display = formatNumber(totals.gadsLeadGoal);
                            if (col.key === "gadsLeads") display = formatNumberOne(totals.gadsLeads);
                            if (col.key === "gadsCpl") display = formatCurrency(totals.gadsCpl);
                            if (col.key === "metaBudget") display = formatCurrency(totals.metaBudget);
                            if (col.key === "metaSpent") display = formatCurrency(totals.metaSpent);
                            if (col.key === "metaLeadGoal") display = formatNumber(totals.metaLeadGoal);
                            if (col.key === "metaLeads") display = formatNumberOne(totals.metaLeads);
                            if (col.key === "metaCpl") display = formatCurrency(totals.metaCpl);
                            const boundaryClass =
                              col.key === "gadsBudget"
                                ? "boundary-left"
                                : col.key === "metaBudget"
                                ? "boundary-meta"
                                : "";
                            return (
                              <td key={col.key} className={boundaryClass}>
                                {display}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })()}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="consolidated-charts">
          <div className="consolidated-chart-card">
            <h3>Goal vs Achieved Leads MTD ({dateLabel})</h3>
            <div className="consolidated-chart-container">
              <Bar data={leadsChartData} options={leadsChartOptions} />
            </div>
          </div>
          <div className="consolidated-chart-card">
            <h3>CPL by Dealership</h3>
            <div className="consolidated-chart-container">
              <Bar data={cplChartData} options={cplChartOptions} />
            </div>
          </div>
        </div>

        <div className="consolidated-footer">
          Source: Google Ads API + Meta Marketing API | Date range {dateLabel}
        </div>
      </div>
    </div>
  );
}
